import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import Base from "../admin_components/Admin_Base";
import { Button, Card, Row, Col, Badge } from "react-bootstrap";
import { Breadcrumbs } from "@mui/material";
import Link from "@mui/material/Link";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import authAxios from "../Auth/useAxios";
import { ADMIN_API_BASE_URL } from "../Auth/Api";
import { ClipLoader } from "react-spinners";
import EditIcon from "@mui/icons-material/Edit";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";

const EmployeeView = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [employee, setEmployee] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchEmployeeDetails();
  }, [id]);

  const fetchEmployeeDetails = async () => {
    try {
      const response = await authAxios.get(
        `${ADMIN_API_BASE_URL}/main/admin/employee/${id}`
      );
      setEmployee(response.data);
      console.log(response.data);
    } catch (error) {
      console.error("Error fetching employee details:", error);
      setError("Failed to fetch employee details");
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = () => {
    navigate(`/admin/employee-edit/${id}`);
  };

  const handleBack = () => {
    navigate("/admin/admin_employeelist");
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    return new Date(dateString).toLocaleDateString("en-IN");
  };

  if (loading) {
    return (
      <Base>
        <div
          className="d-flex justify-content-center align-items-center"
          style={{ height: "50vh" }}
        >
          <ClipLoader color="darkslategrey" size={50} />
        </div>
      </Base>
    );
  }

  if (error) {
    return (
      <Base>
        <div className="alert alert-danger text-center mx-3">{error}</div>
      </Base>
    );
  }

  if (!employee) {
    return (
      <Base>
        <div className="alert alert-warning text-center mx-3">
          Employee not found
        </div>
      </Base>
    );
  }

  return (
    <Base>
      <div className="container-fluid pt-3 mt-5">
        <Breadcrumbs
          separator={<NavigateNextIcon fontSize="small" />}
          aria-label="breadcrumb"
        >
          <Link
            underline="hover"
            color="inherit"
            href="/admin/dashboard"
            sx={{ color: "darkslategrey", fontWeight: "bold" }}
          >
            Dashboard
          </Link>
          <Link
            underline="hover"
            color="inherit"
            href="/admin/admin_employee_list"
            sx={{ color: "darkslategrey", fontWeight: "bold" }}
          >
            Employee List
          </Link>
          <Link
            underline="none"
            color="inherit"
            sx={{ color: "darkslategrey", fontWeight: "bold" }}
          >
            View Employee
          </Link>
        </Breadcrumbs>
      </div>

      <div className="container-fluid mt-3">
        <div className="d-flex justify-content-between align-items-center mb-4">
          <h2 className="fw-bold text-dark">Employee Details</h2>
          <div className="d-flex gap-2">
            <Button variant="outline-secondary" onClick={handleBack}>
              <ArrowBackIcon className="me-1" /> Back to List
            </Button>
            <Button variant="primary" onClick={handleEdit}>
              <EditIcon className="me-1" /> Edit Employee
            </Button>
          </div>
        </div>

        <Card className="mb-4 shadow-sm">
          <Card.Header className="bg-light">
            <Card.Title className="mb-0 fw-bold">
              Personal Information
            </Card.Title>
          </Card.Header>
          <Card.Body>
            <Row>
              <Col md={6}>
                <div className="d-flex justify-content-between align-items-start border-bottom py-2">
                  <span className="fw-bold text-dark">Employee ID:</span>
                  <span className="text-end">
                    {employee.employeeReferenceId}
                  </span>
                </div>
                <div className="d-flex justify-content-between align-items-start border-bottom py-2">
                  <span className="fw-bold text-dark">Full Name:</span>
                  <span className="text-end">{employee.fullName}</span>
                </div>
                <div className="d-flex justify-content-between align-items-start border-bottom py-2">
                  <span className="fw-bold text-dark">Date of Birth:</span>
                  <span className="text-end">
                    {formatDate(employee.dateOfBirth)}
                  </span>
                </div>
                <div className="d-flex justify-content-between align-items-start border-bottom py-2">
                  <span className="fw-bold text-dark">Email:</span>
                  <span className="text-end">{employee.email}</span>
                </div>
              </Col>
              <Col md={6}>
                <div className="d-flex justify-content-between align-items-start border-bottom py-2">
                  <span className="fw-bold text-dark">Mobile:</span>
                  <span className="text-end">{employee.mobile}</span>
                </div>
                <div className="d-flex justify-content-between align-items-start border-bottom py-2">
                  <span className="fw-bold text-dark">Designation:</span>
                  <span className="text-end">{employee.designation}</span>
                </div>
                <div className="d-flex justify-content-between align-items-start border-bottom py-2">
                  <span className="fw-bold text-dark">Location:</span>
                  <span className="text-end">{employee.location}</span>
                </div>
                <div className="d-flex justify-content-between align-items-start border-bottom py-2">
                  <span className="fw-bold text-dark">Status:</span>
                  <Badge bg={employee.active ? "success" : "secondary"}>
                    {employee.active ? "Active" : "Inactive"}
                  </Badge>
                </div>
              </Col>
            </Row>
            <div className="mt-3">
              <div className="border-bottom py-2">
                <div className="fw-bold text-dark mb-1">Address:</div>
                <div className="text-muted">{employee.address}</div>
              </div>
            </div>
          </Card.Body>
        </Card>

        {/* Manager Information */}
        {employee.manager && (
          <Card className="mb-4 shadow-sm">
            <Card.Header className="bg-light">
              <Card.Title className="mb-0 fw-bold">
                Manager Information
              </Card.Title>
            </Card.Header>
            <Card.Body>
              <Row>
                <Col md={6}>
                  <div className="d-flex justify-content-between align-items-start border-bottom py-2">
                    <span className="fw-bold text-dark">Manager Name:</span>
                    <span className="text-end">
                      {employee.manager.fullName}
                    </span>
                  </div>
                </Col>
                <Col md={6}>
                  <div className="d-flex justify-content-between align-items-start border-bottom py-2">
                    <span className="fw-bold text-dark">Manager ID:</span>
                    <span className="text-end">
                      {employee.manager.employeeReferenceId}
                    </span>
                  </div>
                </Col>
              </Row>
            </Card.Body>
          </Card>
        )}

        {/* Document Information */}
        <Card className="mb-4 shadow-sm">
          <Card.Header className="bg-light">
            <Card.Title className="mb-0 fw-bold">
              Document Information
            </Card.Title>
          </Card.Header>
          <Card.Body>
            <div className="d-flex justify-content-between align-items-start border-bottom py-2">
              <span className="fw-bold text-dark">Document Name:</span>
              <span className="text-end">{employee.documentName || "N/A"}</span>
            </div>
            {employee.documentFile && (
              <div className="d-flex justify-content-between align-items-center border-bottom py-2">
                <span className="fw-bold text-dark">Document File:</span>
                <a
                  href={employee.documentFile}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn btn-outline-primary btn-sm"
                >
                  View Document
                </a>
              </div>
            )}
          </Card.Body>
        </Card>
      </div>
    </Base>
  );
};

export default EmployeeView;
